import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import type { LayoutState, SkinType, LayoutType } from "@/types";

// theme config import
import themeConfig from "@/configs/themeConfig";

const isClient = typeof window !== "undefined";

const initialDarkMode = (): boolean => {
    if (isClient) {
        const item = window.localStorage.getItem("darkMode");
        return item ? JSON.parse(item) : themeConfig.layout.darkMode;
    }
    return themeConfig.layout.darkMode;
};

const initialSidebarCollapsed = (): boolean => {
    if (isClient) {
        const item = window.localStorage.getItem("sidebarCollapsed");
        return item ? JSON.parse(item) : themeConfig.layout.menu.isCollapsed;
    }
    return themeConfig.layout.menu.isCollapsed;
};

const initialSemiDarkMode = (): boolean => {
    if (isClient) {
        const item = window.localStorage.getItem("semiDarkMode");
        return item ? JSON.parse(item) : themeConfig.layout.semiDarkMode;
    }
    return themeConfig.layout.semiDarkMode;
};

const initialRtl = (): boolean => {
    if (isClient) {
        const item = window.localStorage.getItem("direction");
        return item ? JSON.parse(item) : themeConfig.layout.isRTL;
    }
    return themeConfig.layout.isRTL;
};

const initialSkin = (): SkinType => {
    if (isClient) {
        const item = window.localStorage.getItem("skin");
        return item ? JSON.parse(item) : themeConfig.layout.skin;
    }
    return themeConfig.layout.skin;
};

const initialType = (): LayoutType => {
    if (isClient) {
        const item = window.localStorage.getItem("type");
        return item ? JSON.parse(item) : themeConfig.layout.type;
    }
    return themeConfig.layout.type;
};

const initialMonochrome = (): boolean => {
    if (isClient) {
        const item = window.localStorage.getItem("monochrome");
        return item ? JSON.parse(item) : themeConfig.layout.isMonochrome;
    }
    return themeConfig.layout.isMonochrome;
};

const initialState: LayoutState = {
    isRTL: initialRtl(),
    darkMode: initialDarkMode(),
    isCollapsed: initialSidebarCollapsed(),
    customizer: themeConfig.layout.customizer,
    semiDarkMode: initialSemiDarkMode(),
    skin: initialSkin(),
    contentWidth: themeConfig.layout.contentWidth,
    type: initialType(),
    menuHidden: themeConfig.layout.menu.isHidden,
    navBarType: themeConfig.layout.navBarType,
    footerType: themeConfig.layout.footerType,
    mobileMenu: themeConfig.layout.mobileMenu,
    isMonochrome: initialMonochrome(),
};

export const layoutSlice = createSlice({
    name: "layout",
    initialState,
    reducers: {
        // handle dark mode
        handleDarkMode: (state, action: PayloadAction<boolean>) => {
            state.darkMode = action.payload;
            if (isClient) {
                window.localStorage.setItem("darkMode", String(action.payload));
            }
        },
        // handle sidebar collapsed
        handleSidebarCollapsed: (state, action: PayloadAction<boolean>) => {
            state.isCollapsed = action.payload;
            if (isClient) {
                window.localStorage.setItem("sidebarCollapsed", String(action.payload));
            }
        },
        // handle customizer
        handleCustomizer: (state, action: PayloadAction<boolean>) => {
            state.customizer = action.payload;
        },
        // handle semiDark
        handleSemiDarkMode: (state, action: PayloadAction<boolean>) => {
            state.semiDarkMode = action.payload;
            if (isClient) {
                window.localStorage.setItem("semiDarkMode", String(action.payload));
            }
        },
        // handle rtl
        handleRtl: (state, action: PayloadAction<boolean>) => {
            state.isRTL = action.payload;
            if (isClient) {
                window.localStorage.setItem(
                    "direction",
                    JSON.stringify(action.payload),
                );
            }
        },
        // handle skin
        handleSkin: (state, action: PayloadAction<SkinType>) => {
            state.skin = action.payload;
            if (isClient) {
                window.localStorage.setItem(
                    "skin",
                    JSON.stringify(action.payload),
                );
            }
        },
        // handle content width
        handleContentWidth: (state, action: PayloadAction<LayoutState['contentWidth']>) => {
            state.contentWidth = action.payload;
        },
        // handle type
        handleType: (state, action: PayloadAction<LayoutType>) => {
            state.type = action.payload;
            if (isClient) {
                window.localStorage.setItem(
                    "type",
                    JSON.stringify(action.payload),
                );
            }
        },
        // handle menu hidden
        handleMenuHidden: (state, action: PayloadAction<boolean>) => {
            state.menuHidden = action.payload;
        },
        // handle navbar type
        handleNavBarType: (state, action: PayloadAction<LayoutState['navBarType']>) => {
            state.navBarType = action.payload;
        },
        // handle footer type
        handleFooterType: (state, action: PayloadAction<LayoutState['footerType']>) => {
            state.footerType = action.payload;
        },
        handleMobileMenu: (state, action: PayloadAction<boolean>) => {
            state.mobileMenu = action.payload;
        },
        handleMonoChrome: (state, action: PayloadAction<boolean>) => {
            state.isMonochrome = action.payload;
            if (isClient) {
                window.localStorage.setItem(
                    "monochrome",
                    JSON.stringify(action.payload),
                );
            }
        },
    },
});

export const {
    handleDarkMode,
    handleSidebarCollapsed,
    handleCustomizer,
    handleSemiDarkMode,
    handleRtl,
    handleSkin,
    handleContentWidth,
    handleType,
    handleMenuHidden,
    handleNavBarType,
    handleFooterType,
    handleMobileMenu,
    handleMonoChrome,
} = layoutSlice.actions;

export default layoutSlice.reducer;
